/**
 * Field metadata validation functions
 * Validates individual form field properties (label, elementId, etc.)
 */
import { SxlEvaluator } from '@saviynt/savxl';

import {
  CHARACTER_COUNT,
  NUMERICAL_VALUE,
  TNC_TYPES,
  validateUrl,
} from '../../../../shared/constants';

import {
  ELEMENT_ID_VALIDATION,
  ERROR_MESSAGE_VALIDATION,
  FIELD_LABEL_VALIDATION,
  FIXED_GROUP_OPTION_LABEL_VALIDATION,
  FIXED_GROUP_OPTION_VALUE_VALIDATION,
  HELPTEXT_VALIDATION,
  PLACEHOLDER_VALIDATION,
  RICH_TEXT_VALIDATION,
  TERMS_LINK_VALIDATION,
  TNC_MESSAGE_VALIDATION,
  TOOLTIP_VALIDATION,
} from './validationRules';

// Helper function to check for HTML tags
const containsHTML = (text: string): boolean => {
  const htmlRegex = /<[^>]*>/g;

  return htmlRegex.test(text);
};

// Helper function to trim leading/trailing whitespace
const trimWhitespace = (text: string): string => text.trim();

const evaluator = new SxlEvaluator();

/**
 * Validates field label
 * Max 50 chars (200 for radio/checkbox), letters/numbers/spaces/common punctuation, starts with letter/number
 */
export const validateFieldLabel = (
  value: string,
  fieldType?: string,
  options?: { skipHtmlCheck?: boolean }
): string | null => {
  const trimmed = trimWhitespace(value);

  if (trimmed.length === 0) {
    return FIELD_LABEL_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  if (trimmed.length > FIELD_LABEL_VALIDATION.MAX_LENGTH) {
    return FIELD_LABEL_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  // Must start with letter or number
  if (!FIELD_LABEL_VALIDATION.START_REGEX.test(trimmed)) {
    return FIELD_LABEL_VALIDATION.ERROR_MESSAGES.INVALID_START;
  }

  // Skip character validation, HTML check, and angle bracket check for loaded data
  // When skipHtmlCheck is true, we allow stored HTML labels to pass validation
  // This is needed because the REGEX doesn't allow <, >, or / characters
  if (!options?.skipHtmlCheck) {
    // Allow only specified characters (letters, numbers, spaces, and common punctuation)
    if (!FIELD_LABEL_VALIDATION.REGEX.test(trimmed)) {
      return FIELD_LABEL_VALIDATION.ERROR_MESSAGES.INVALID_CHARACTERS;
    }

    // Check for HTML tags
    // Note: HTML tags are stripped on input, but validation provides defense in depth
    if (containsHTML(trimmed)) {
      return FIELD_LABEL_VALIDATION.ERROR_MESSAGES.NO_HTML;
    }

    // Check for angle brackets
    const hasAngleBrackets = /<|>/.test(value);

    if (hasAngleBrackets) {
      return FIELD_LABEL_VALIDATION.ERROR_MESSAGES.NO_BRACKETS;
    }
  }

  return null;
};

/**
 * Validates element ID
 * Mandatory, unique, max 50 chars, lowercase alphanumeric + underscore only, must start with alphanumeric
 */
export const validateElementId = (
  value: string,
  existingIds: string[] = []
): string | null => {
  if (!value.trim()) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  if (value.includes(' ')) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.INVALID_CHARACTERS;
  }

  const trimmed = trimWhitespace(value);

  if (trimmed.length > ELEMENT_ID_VALIDATION.MAX_LENGTH) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  // Must start with alphanumeric character
  if (!ELEMENT_ID_VALIDATION.START_REGEX.test(trimmed)) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.INVALID_START;
  }

  // Must be lowercase alphanumeric + underscore only
  if (!ELEMENT_ID_VALIDATION.REGEX.test(trimmed)) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.INVALID_CHARACTERS;
  }

  // Check for reserved words
  const reservedWords = ELEMENT_ID_VALIDATION.RESERVED_WORDS;
  const lowerTrimmed = trimmed.toLowerCase();

  if (reservedWords.some((word) => word === lowerTrimmed)) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.RESERVED_WORD;
  }

  // Check uniqueness
  if (existingIds.includes(trimmed)) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.NOT_UNIQUE;
  }

  // Check for HTML tags
  if (containsHTML(trimmed)) {
    return ELEMENT_ID_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  return null;
};

/**
 * Validates placeholder text
 * Optional, max 40 chars
 */
export const validatePlaceholder = (value: string): string | null => {
  if (!value) {
    return null;
  } // Optional field

  const trimmed = trimWhitespace(value);

  if (trimmed.length > PLACEHOLDER_VALIDATION.MAX_LENGTH) {
    return PLACEHOLDER_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  if (containsHTML(trimmed)) {
    return PLACEHOLDER_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  return null;
};

/**
 * Validates help text
 * Optional, max 100 chars
 */
export const validateHelptext = (value: string): string | null => {
  if (!value) {
    return null;
  } // Optional field

  const trimmed = trimWhitespace(value);

  if (trimmed.length > HELPTEXT_VALIDATION.MAX_LENGTH) {
    return HELPTEXT_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  if (containsHTML(trimmed)) {
    return HELPTEXT_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  const hasAngleBrackets = /<|>/.test(value);

  if (hasAngleBrackets) {
    return HELPTEXT_VALIDATION.ERROR_MESSAGES.NO_BRACKETS;
  }

  return null;
};

/**
 * Validates error message
 * @param value - The error message text
 * @param isRequired - Whether the field is required (for regex validation)
 * @returns Error message or null if valid
 */
export const validateErrorMessage = (
  value: string,
  isRequired = false
): string | null => {
  const trimmed = trimWhitespace(value);

  // Check if required and empty
  if (isRequired && !trimmed) {
    return 'Error message is required';
  }

  // If optional and empty, no error
  if (!trimmed) {
    return null;
  }

  // Max 100 characters validation
  if (trimmed.length > ERROR_MESSAGE_VALIDATION.MAX_LENGTH) {
    return ERROR_MESSAGE_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  // No HTML tags
  if (containsHTML(trimmed)) {
    return ERROR_MESSAGE_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  const hasAngleBrackets = /<|>/.test(value);

  // No Angle Brakets
  if (hasAngleBrackets) {
    return ERROR_MESSAGE_VALIDATION.ERROR_MESSAGES.NO_BRACKETS;
  }

  return null;
};

/**
 * Validates fixed group option label
 * Required, max 100 chars, no HTML, no angle brackets
 */
export const validateFixedGroupOptionLabel = (value: string): string | null => {
  const trimmed = trimWhitespace(value);

  if (!trimmed) {
    return FIXED_GROUP_OPTION_LABEL_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  if (trimmed.length > FIXED_GROUP_OPTION_LABEL_VALIDATION.MAX_LENGTH) {
    return FIXED_GROUP_OPTION_LABEL_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  if (containsHTML(trimmed)) {
    return FIXED_GROUP_OPTION_LABEL_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  const hasAngleBrackets = /<|>/.test(value);

  if (hasAngleBrackets) {
    return FIXED_GROUP_OPTION_LABEL_VALIDATION.ERROR_MESSAGES.NO_BRACKETS;
  }

  return null;
};

/**
 * Validates fixed group option value
 * Required, max 200 chars
 */
export const validateFixedGroupOptionValue = (value: string): string | null => {
  const trimmed = trimWhitespace(value);

  if (!trimmed) {
    return FIXED_GROUP_OPTION_VALUE_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  if (trimmed.length > FIXED_GROUP_OPTION_VALUE_VALIDATION.MAX_LENGTH) {
    return FIXED_GROUP_OPTION_VALUE_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  return null;
};

/**
 * Validates tooltip text
 * Optional, max 80 chars
 */
export const validateTooltip = (value: string): string | null => {
  if (!value) {
    return null;
  } // Optional field

  const trimmed = trimWhitespace(value);

  if (trimmed.length > TOOLTIP_VALIDATION.MAX_LENGTH) {
    return TOOLTIP_VALIDATION.ERROR_MESSAGES.MAX_LENGTH;
  }

  if (containsHTML(trimmed)) {
    return TOOLTIP_VALIDATION.ERROR_MESSAGES.NO_HTML;
  }

  const hasAngleBrackets = /<|>/.test(value);

  if (hasAngleBrackets) {
    return TOOLTIP_VALIDATION.ERROR_MESSAGES.NO_BRACKETS;
  }

  return null;
};

/**
 * Validates tooltip text
 * Optional, max 80 chars
 */
export const validateExpression = (value: string): string | null => {
  if (!value) {
    return null;
  } // Optional field

  // Validate that all ${...} variable references contain valid identifiers
  // Valid: ${fieldName}, ${user.name}, ${items[0].value}
  // Invalid: ${$ text}, ${123field}, ${field name}
  const variablePattern = /\$\{([^}]*)\}/g;
  let match = variablePattern.exec(value);

  while (match !== null) {
    const varContent = match[1].trim();
    // Allow: identifiers with dot notation and array access (e.g. field, user.name, items[0])
    const validIdentifier = /^[a-zA-Z_][a-zA-Z0-9_.[\]]*$/.test(varContent);

    if (!validIdentifier) {
      return `Invalid variable reference: \${${varContent}}. Variable names must start with a letter or underscore and contain only letters, numbers, underscores, dots, or brackets.`;
    }

    match = variablePattern.exec(value);
  }

  try {
    evaluator.validateSyntax(value);
  } catch (e) {
    if (e instanceof Error) {
      // Clean non-printable / replacement characters from savxl error messages
      const cleaned = e.message.replace(/[^\x20-\x7E]/g, '').trim();

      return cleaned || 'Invalid expression syntax';
    }

    return 'Unknown error';
  }

  return null;
};

/**
 * Validates expression value when expression is enabled
 * Required when expression toggle is enabled
 */
export const validateExpressionValue = (value: string): string | null => {
  const trimmed = trimWhitespace(value);

  if (!trimmed) {
    return 'Expression value is required when expression is enabled';
  }

  // If non-empty, proceed with syntax validation
  return validateExpression(value);
};

/**
 * Validates regex pattern syntax
 * Required when regex validation is enabled
 */
export const validateRegexPattern = (value: string): string | null => {
  const trimmed = trimWhitespace(value);

  if (!trimmed) {
    return 'Regex pattern is required when validation is enabled';
  }

  try {
    // eslint-disable-next-line no-new
    new RegExp(trimmed);

    return null;
  } catch (error) {
    // Extract the specific error detail from the native SyntaxError
    // e.g. "Invalid regular expression: /pattern/: Unterminated group"
    const nativeMsg = error instanceof SyntaxError ? error.message : undefined;
    const detailMatch = nativeMsg?.match(/:\s*([^:]+)$/);
    // Strip non-printable / replacement characters for clean display
    const detail = detailMatch?.[1]
      ?.trim()
      .replace(/[^\x20-\x7E]/g, '')
      .trim();

    return detail ? `Invalid regex: ${detail}` : 'Invalid regex pattern';
  }
};

/**
 * Validates terms link URL
 * Must begin with http:// or https:// and must not contain spaces
 */
export const validateTnCLink = (
  value: string,
  tncType: string
): string | null => {
  if (tncType !== TNC_TYPES.URL) {
    return null;
  }

  const trimmed = trimWhitespace(value);

  if (!trimmed) {
    return TERMS_LINK_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  // Use comprehensive URL validation (format, length, private network blocking)
  const urlError = validateUrl(trimmed);

  if (urlError) {
    return urlError;
  }

  return null;
};

/**
 * Validates inline link message
 * Must contain text wrapped in {} to create a clickable link
 * Must not have nested braces or unmatched braces
 */
export const validateTnCMessage = (value: string): string | null => {
  const trimmed = trimWhitespace(value);

  if (!trimmed) {
    return TNC_MESSAGE_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  if (!TNC_MESSAGE_VALIDATION.REGEX.test(trimmed)) {
    return TNC_MESSAGE_VALIDATION.ERROR_MESSAGES.INVALID_FORMAT;
  }

  return null;
};

/**
 * Validates richText for terms and conditions
 * Must not be empty when tncType is TEXT
 */
export const validateRichText = (value: unknown): string | null => {
  let richTextObj = value;

  // Handle stringified richText
  if (typeof value === 'string') {
    try {
      richTextObj = JSON.parse(value);
    } catch {
      return RICH_TEXT_VALIDATION.ERROR_MESSAGES.REQUIRED;
    }
  }

  if (
    !richTextObj ||
    (typeof richTextObj === 'object' &&
      Object.keys(richTextObj).length === 0) ||
    (richTextObj &&
      typeof richTextObj === 'object' &&
      'ops' in richTextObj &&
      Array.isArray(richTextObj.ops) &&
      richTextObj.ops.length === 1 &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      richTextObj.ops[0].insert === '\n')
  ) {
    return RICH_TEXT_VALIDATION.ERROR_MESSAGES.REQUIRED;
  }

  return null;
};

/**
 * Validates unified limit (min-max) values
 * Checks if min value is greater than max value
 */
export const validateUnifiedLimit = (
  minValue: string | number | undefined,
  maxValue: string | number | undefined,
  isNumeric = false
): string | null => {
  const min = typeof minValue === 'string' ? parseInt(minValue, 10) : minValue;
  const max = typeof maxValue === 'string' ? parseInt(maxValue, 10) : maxValue;

  if (
    min !== undefined &&
    max !== undefined &&
    !Number.isNaN(min) &&
    !Number.isNaN(max) &&
    min > 0 &&
    max > 0 &&
    min > max
  ) {
    return isNumeric
      ? NUMERICAL_VALUE.ERROR_MESSAGES.MIN_GREATER_THAN_MAX
      : CHARACTER_COUNT.ERROR_MESSAGES.MIN_GREATER_THAN_MAX;
  }

  return null;
};
